import React, { useContext, useState, useMemo } from "react";
import * as cadviewer from "cadviewer";
import { motion, useDragControls } from "motion/react";
import { CadviewerContext, RoomData } from "../../context/Cadviewer.Context";
import Tooltip from "../Tooltip";
import useConfig from "../../demos/hooks/useConfig";
import { useDepartmentPanelResize, useDepartmentPanelPosition } from "./hooks";
import { calculateDragConstraints, calculatePanelStyle } from "./utils";
import { useDepartmentPanelStore } from "./stores/departmentPanelStore";

// Department Icon Component
const DepartmentIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-6 h-6"
  >
    <path d="M2 20v-8a2 2 0 012-2h16a2 2 0 012 2v8" />
    <path d="M4 10V6a2 2 0 012-2h12a2 2 0 012 2v4" />
    <path d="M12 4v6" />
    <path d="M2 20h20" />
    <path d="M6 20v-4" />
    <path d="M18 20v-4" />
  </svg>
);

// Close Icon Component
const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Highlight Icon
const HighlightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
    <path d="M12 2a9 9 0 1 0 9 9" />
    <path d="M18 5l1 1" />
    <path d="M15 2l1 1" />
    <path d="M21 8l1 1" />
  </svg>
);

// Clear Icon
const ClearIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
  </svg>
);

// Pin Icon
const PinIcon: React.FC<{ isPinned: boolean }> = ({ isPinned }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill={isPinned ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={isPinned ? "rotate-45" : ""}
  >
    <line x1="12" y1="17" x2="12" y2="22" />
    <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
  </svg>
);

interface Department {
  dp_id: string;
  dp_name: string;
  roomCount: number;
  rooms: RoomData[];
}

interface DepartmentPanelProps {
  isPinned?: boolean;
  onClose?: () => void;
  onTogglePin?: () => void;
}

const DepartmentPanel: React.FC<DepartmentPanelProps> = ({ isPinned: propIsPinned, onClose, onTogglePin }) => {
  const { config } = useConfig();
  const { roomsData } = useContext(CadviewerContext);
  const { isOpen, isPinned: storeIsPinned, toggleOpen, togglePin } = useDepartmentPanelStore();
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Use prop isPinned if provided (for pinned mode rendering from App.tsx), otherwise use store
  const isPinned = propIsPinned !== undefined ? propIsPinned : storeIsPinned;
  const handleTogglePin = onTogglePin ?? togglePin;

  // Drag and resize hooks
  const { panelWidth, panelHeight, panelRef, setIsResizing, setIsResizingHeight } =
    useDepartmentPanelResize(320, 500);
  const { x, y } = useDepartmentPanelPosition(isOpen);
  const dragControls = useDragControls();

  // Memoized calculations
  const dragConstraints = useMemo(
    () => calculateDragConstraints(panelWidth, panelHeight),
    [panelWidth, panelHeight]
  );

  const panelStyle = useMemo(
    () => calculatePanelStyle(panelWidth, panelHeight, isOpen),
    [panelWidth, panelHeight, isOpen]
  );

  // Extract unique departments from roomsData
  const departments = useMemo<Department[]>(() => {
    if (!roomsData || roomsData.length === 0) return [];

    const deptMap = new Map<string, Department>();

    roomsData.forEach((room) => {
      const dpId = room.dp_id;
      const dpName = room.dp_name;

      if (!deptMap.has(dpId)) {
        deptMap.set(dpId, {
          dp_id: dpId,
          dp_name: dpName,
          roomCount: 0,
          rooms: [],
        });
      }

      const dept = deptMap.get(dpId)!;
      dept.roomCount++;
      dept.rooms.push(room);
    });

    return Array.from(deptMap.values()).sort((a, b) =>
      a.dp_name.localeCompare(b.dp_name)
    );
  }, [roomsData]);

  // Filter departments based on search query
  const filteredDepartments = useMemo(() => {
    if (!searchQuery.trim()) return departments;
    const query = searchQuery.toLowerCase();
    return departments.filter(
      (dept) =>
        dept.dp_name.toLowerCase().includes(query) ||
        dept.dp_id.toLowerCase().includes(query)
    );
  }, [departments, searchQuery]);

  // Highlight color for departments
  const highlight_blue = {
    fill: "#0c8dff",
    "fill-opacity": 0.8,
    stroke: "#003366",
    "stroke-width": 2.0,
    "stroke-opacity": 1.0,
    "stroke-linejoin": "round",
  };

  // Highlight rooms for a specific department
  const handleHighlightDepartment = (dept: Department) => {
    // Clear previous highlights
    cadviewer.cvjs_clearSpaceLayer();

    // Highlight all rooms in this department
    dept.rooms.forEach((room) => {
      const roomId = room.rm_id;
      try {
        // Use the CADViewer highlight method with color object
        cadviewer.cvjs_highlightSpace(roomId, highlight_blue);
      } catch (e) {
        if (config.debugMode) console.log("Could not highlight room:", roomId, e);
      }
    });

    setSelectedDepartment(dept.dp_id);
    if (config.debugMode) console.log(`Highlighted ${dept.rooms.length} rooms for department: ${dept.dp_name}`);
  };

  // Clear all highlights
  const handleClearHighlights = () => {
    cadviewer.cvjs_clearSpaceLayer();
    setSelectedDepartment(null);
  };

  // Toggle panel visibility
  const handleClose = () => {
    if (isOpen) {
      // Closing - clear highlights
      handleClearHighlights();
    }
    if (onClose) {
      onClose();
    } else {
      toggleOpen();
    }
  };

  // Don't render if no rooms data
  if (!roomsData || roomsData.length === 0) {
    return null;
  }

  // Pinned mode: render as a simple flex container (rendered inside Panel from App.tsx)
  if (propIsPinned) {
    return (
      <div className="flex flex-col h-full bg-white border-l border-slate-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white p-4 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">Departments</h3>
            <p className="text-xs text-blue-100">
              {departments.length} departments • {roomsData.length} rooms
            </p>
          </div>
          <div className="flex items-center space-x-1">
            {/* Pin button */}
            <button
              onClick={handleTogglePin}
              className={`p-1.5 rounded-full transition-colors ${isPinned
                ? "bg-blue-400 text-white"
                : "hover:bg-blue-600 text-blue-100"
                }`}
              aria-label={isPinned ? "Unpin panel" : "Pin panel"}
              title={isPinned ? "Unpin (enable drag)" : "Pin (disable drag)"}
            >
              <PinIcon isPinned={isPinned} />
            </button>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-blue-600 rounded-full transition-colors"
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-gray-200">
          <input
            type="text"
            placeholder="Search departments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Actions Bar */}
        <div className="p-2 border-b border-gray-200 flex gap-2">
          <button
            onClick={handleClearHighlights}
            disabled={!selectedDepartment}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ClearIcon />
            Clear Highlights
          </button>
        </div>

        {/* Department List */}
        <div className="overflow-y-auto flex-1">
          {filteredDepartments.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              No departments found
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {filteredDepartments.map((dept) => (
                <li
                  key={dept.dp_id}
                  className={`p-3 hover:bg-gray-50 transition-colors ${selectedDepartment === dept.dp_id ? "bg-blue-50" : ""
                    }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium truncate ${selectedDepartment === dept.dp_id
                          ? "text-blue-700"
                          : "text-gray-900"
                          }`}
                        title={dept.dp_name}
                      >
                        {dept.dp_name}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        ID: {dept.dp_id} • {dept.roomCount} room
                        {dept.roomCount > 1 ? "s" : ""}
                      </p>
                    </div>
                    <button
                      onClick={() => handleHighlightDepartment(dept)}
                      className={`ml-2 p-1.5 rounded-md transition-colors ${selectedDepartment === dept.dp_id
                        ? "bg-blue-600 text-white"
                        : "text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                        }`}
                      title="Highlight rooms"
                    >
                      <HighlightIcon />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  // Floating mode render
  return (
    <>
      {/* Floating Button - positioned by parent container */}
      <div className="flex flex-col items-center space-y-2">
        <Tooltip
          text="Departments"
          className="!left-1/2 !-translate-x-full"
        >
          <button
            onClick={toggleOpen}
            className={`p-3 ${isOpen
              ? "bg-gradient-to-r from-blue-600 to-blue-800"
              : "bg-gradient-to-r from-blue-400 to-blue-700"
              } text-white rounded-full shadow-lg hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-transform duration-150 ease-in-out`}
            aria-label="Departments"
            title="View Departments"
          >
            <DepartmentIcon />
          </button>
        </Tooltip>
      </div>

      {/* Department Panel/Drawer */}
      {isOpen && (
        <motion.div
          ref={panelRef}
          drag={!isPinned}
          dragListener={false}
          dragControls={dragControls}
          dragConstraints={dragConstraints}
          dragElastic={0}
          dragMomentum={false}
          className="border border-gray-200"
          style={{
            ...panelStyle,
            x,
            y,
          }}
          animate={{
            x: isOpen ? undefined : panelWidth + 32,
            y: isOpen ? undefined : 0,
          }}
          transition={{
            type: "spring",
            damping: 25,
            stiffness: 300,
          }}
        >
          {/* Resize Handle - Left */}
          {!isPinned && (
            <div
              onMouseDown={() => setIsResizing(true)}
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 8,
                cursor: "ew-resize",
                zIndex: 10,
              }}
              aria-label="Resize panel width"
            />
          )}
          {/* Resize Handle - Bottom */}
          {!isPinned && (
            <div
              onMouseDown={() => setIsResizingHeight(true)}
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: 8,
                cursor: "ns-resize",
                zIndex: 10,
              }}
              aria-label="Resize panel height"
            />
          )}

          {/* Header */}
          <div
            className={`bg-gradient-to-r from-blue-500 to-blue-700 text-white p-4 flex items-center justify-between ${isPinned ? "cursor-default" : "cursor-move"
              }`}
            style={{ userSelect: "none" }}
            onPointerDown={(e) => {
              if (!isPinned) {
                dragControls.start(e);
              }
            }}
          >
            <div>
              <h3 className="font-semibold text-lg">Departments</h3>
              <p className="text-xs text-blue-100">
                {departments.length} departments • {roomsData.length} rooms
              </p>
            </div>
            <div className="flex items-center space-x-1">
              {/* Pin button */}
              <button
                onClick={handleTogglePin}
                className={`p-1.5 rounded-full transition-colors ${isPinned
                  ? "bg-blue-400 text-white"
                  : "hover:bg-blue-600 text-blue-100"
                  }`}
                aria-label={isPinned ? "Unpin panel" : "Pin panel"}
                title={isPinned ? "Unpin (enable drag)" : "Pin (disable drag)"}
              >
                <PinIcon isPinned={isPinned} />
              </button>
              <button
                onClick={handleClose}
                className="p-1 hover:bg-blue-600 rounded-full transition-colors"
                aria-label="Close"
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="p-3 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search departments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Actions Bar */}
          <div className="p-2 border-b border-gray-200 flex gap-2">
            <button
              onClick={handleClearHighlights}
              disabled={!selectedDepartment}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ClearIcon />
              Clear Highlights
            </button>
          </div>

          {/* Department List */}
          <div className="overflow-y-auto flex-1">
            {filteredDepartments.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                No departments found
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {filteredDepartments.map((dept) => (
                  <li
                    key={dept.dp_id}
                    className={`p-3 hover:bg-gray-50 transition-colors ${selectedDepartment === dept.dp_id ? "bg-blue-50" : ""
                      }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm font-medium truncate ${selectedDepartment === dept.dp_id
                            ? "text-blue-700"
                            : "text-gray-900"
                            }`}
                          title={dept.dp_name}
                        >
                          {dept.dp_name}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          ID: {dept.dp_id} • {dept.roomCount} room
                          {dept.roomCount > 1 ? "s" : ""}
                        </p>
                      </div>
                      <button
                        onClick={() => handleHighlightDepartment(dept)}
                        className={`ml-2 p-1.5 rounded-md transition-colors ${selectedDepartment === dept.dp_id
                          ? "bg-blue-600 text-white"
                          : "text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                          }`}
                        title="Highlight rooms"
                      >
                        <HighlightIcon />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </motion.div>
      )}
    </>
  );
};

export default DepartmentPanel;
